import {SkeletonText} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useId} from 'react'

import styles from './SidebarNavigation.module.css'

export interface SidebarNavigationProps {
  children?: React.ReactNode
  isCondensed?: boolean
  'aria-label'?: string
  className?: string
}

/**
 * Main navigation container for sidebar content.
 * Should wrap all sidebar sections and provide consistent styling.
 */
export function SidebarNavigation({
  children,
  isCondensed,
  'aria-label': ariaLabel = 'Navigation',
  className,
}: SidebarNavigationProps) {
  const id = useId()

  return (
    <nav
      aria-label={ariaLabel}
      id={`${id}-navigation`}
      tabIndex={-1}
      className={clsx(styles.container, isCondensed && styles.condensed, className)}
    >
      <div className={styles.scrollingContainer}>
        <div className={styles.section}>{children}</div>
      </div>
    </nav>
  )
}

export interface SidebarSectionProps {
  title?: string
  children: React.ReactNode
  isLoading?: boolean
  isEmpty?: boolean
  viewMoreLink?: React.ReactNode
  trailingAction?: React.ReactNode
}

/**
 * A section within the sidebar navigation.
 * Provides consistent grouping with titles and handles loading/empty states.
 */
export function SidebarSection({
  title,
  children,
  isLoading,
  isEmpty,
  viewMoreLink,
  trailingAction,
}: SidebarSectionProps) {
  const id = useId()

  return (
    <div className={styles.sectionContainer}>
      {title && (
        <div className={styles.titleContainer}>
          <h4 id={id} className={styles.title}>
            {title}
          </h4>
          {trailingAction ? trailingAction : null}
        </div>
      )}
      {isEmpty && isLoading ? (
        <SkeletonText lines={5} />
      ) : isEmpty ? null : (
        <>
          <ul className={styles.list}>{children}</ul>
          {viewMoreLink}
        </>
      )}
    </div>
  )
}
